// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SectionContainer, SectionTitle } from "@awc/App/Widgets/DesignSystem/DesignSystem.helpers";
import { dataTable } from "@awc/App/Widgets/DesignSystem/DummyData/DummyData";
import { dummyProductContext } from "@awc/App/Widgets/DesignSystem/DummyData/DummyProduct";
import { DataTableCellProps } from "@awc/MobiusOverrides/DataTable/DataTableCell";
import { DataTableRowProps } from "@awc/MobiusOverrides/DataTable/DataTableRow";
import { color, xScreen } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import DataTable from "@insite/mobius/DataTable/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface TableTabStyles {
    container?: InjectableCss;
    titleGridItem?: GridItemProps;
    gridItem?: GridItemProps;
    sectionContainer?: { gridContainer?: GridContainerProps };
    dataTableCell?: DataTableCellProps;
    dataTableRow?: DataTableRowProps;
    pdpTableGridItem?: GridItemProps;
}

const tableTabStyles: TableTabStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            row-gap: 24px;
        `,
    },
    titleGridItem: {
        width: 12,
    },
    gridItem: {
        width: 12,
    },
    sectionContainer: {
        gridContainer: {
            gap: 0,
        },
    },
    dataTableCell: {
        css: css`
            border-bottom: 1px solid ${color("common.ui2")};
        `,
    },
    dataTableRow: {
        css: css`
            &:nth-child(even) {
                background: ${color("common.ui2")};
            }
        `,
    },
    pdpTableGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            &&& {
                max-width: 562px;
                ${xScreen("lte", "md")} {
                    max-width: 100%;
                }
            }
        `,
    },
};

const styles = tableTabStyles;
const TableTab: FC = props => {
    return (
        <StyledWrapper {...styles.container}>
            <SectionContainer extendedStyles={styles.sectionContainer}>
                <GridItem {...styles.titleGridItem}>
                    <SectionTitle>{translate("UI : Tables")}</SectionTitle>
                </GridItem>
                <GridItem width={12}>
                    <DataTable>
                        <DataTableHead>
                            <DataTableHeader onSortClick={() => logger.log("sorting")} tight>
                                Date
                            </DataTableHeader>
                            <DataTableHeader onSortClick={() => logger.log("sorting")} tight title="Order Number">
                                Order #
                            </DataTableHeader>
                            <DataTableHeader tight>Ship To</DataTableHeader>
                            <DataTableHeader onSortClick={() => logger.log("sorting")} sorted="descending" tight>
                                Status
                            </DataTableHeader>
                            <DataTableHeader
                                onSortClick={() => logger.log("sorting")}
                                tight
                                title="Purchase Order Number"
                            >
                                PO #
                            </DataTableHeader>
                            <DataTableHeader tight>Order Total</DataTableHeader>
                            <DataTableHeader tight title="reorder" />
                        </DataTableHead>
                        <DataTableBody>
                            {dataTable.map(({ date, order, shipTo, status, po, total }) => (
                                <DataTableRow key={order} {...styles.dataTableRow}>
                                    <DataTableCell {...styles.dataTableCell} {...{ header: "Date" }}>
                                        {date}
                                    </DataTableCell>
                                    <DataTableCell {...styles.dataTableCell} {...{ header: "Order #" }}>
                                        <Link href="#" {...props}>
                                            {order}
                                        </Link>
                                    </DataTableCell>
                                    <DataTableCell {...styles.dataTableCell} {...{ header: "Ship To" }}>
                                        {shipTo}
                                    </DataTableCell>
                                    <DataTableCell {...styles.dataTableCell} {...{ header: " Status" }}>
                                        {status}
                                    </DataTableCell>
                                    <DataTableCell {...styles.dataTableCell} {...{ header: "PO #" }}>
                                        {po}
                                    </DataTableCell>
                                    <DataTableCell {...styles.dataTableCell} {...{ header: "Order Total" }}>
                                        ${total}
                                    </DataTableCell>
                                    <DataTableCell {...styles.dataTableCell} alignX="right">
                                        <Button variant="secondary" sizeVariant="small">
                                            Reorder
                                        </Button>
                                    </DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                </GridItem>
            </SectionContainer>
            <SectionContainer extendedStyles={styles.sectionContainer}>
                <GridItem {...styles.titleGridItem}>
                    <SectionTitle>{translate("UI : Tables : PDP")}</SectionTitle>
                </GridItem>
                <GridItem {...styles.pdpTableGridItem}>
                    <ProductContext.Provider value={dummyProductContext}>
                        {createWidgetElement("ProductDetails/ProductDetailsAttributes", {
                            fields: {},
                            showAttributesTextLabel: false,
                            showDownloadAttributesButton: false,
                        } as any)}
                    </ProductContext.Provider>
                </GridItem>
            </SectionContainer>
        </StyledWrapper>
    );
};

export default TableTab;
